import { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { Transition } from "@headlessui/react";
import Swal from "sweetalert2";

// Icons
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, ArrowsUpDownIcon, ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";

// Contexts
import PSAuthContext from "../../../context/psikotest/AuthContext";
import LoaderContext from "../../../context/psikotest/LoaderContext";
import SnackbarContext from "../../../context/psikotest/SnackbarContext";

// Helpers
import errorHandler from "../../../helper/psikotest/errorHandler";

// Controller
import Assessment from "../../../controller/psikotest/psikotest_system/assessment";
import AssessmentBehaviouralSubtest from "../../../controller/psikotest/psikotest_system/assessment_behavioural_subtest";

// Modals
import FormBehaviouralSubtest from "./modal/FormBehaviouralSubtest";
import ModalBehaviouralSubtestSession from "./modal/ModalBehaviouralSubtestSession";
import ModalBehaviouralScore from "./modal/ModalBehaviouralScore";
import {
    activeTargetKey,
    isActiveTarget,
} from "./activeSubtestControl";

function ListBehavioural(props: {
    assessment_id: number;
    editable: boolean;
    doc: TPSAssessment;
    isPsikolog: boolean;
} & TPSActiveSubtestControlProps) {
    // Contexts
    const { user } = useContext(PSAuthContext);
    const { setMessage } = useContext(LoaderContext);
    const { setNotif } = useContext(SnackbarContext);
    // is admin
    const isAdmin = user.roles === "1";

    // helper state
    // list assessment behavioural 
    const [assessment_behavioural_subtest, setAssessmentBehaviouralSubtest] = useState<TPSAssessmentBehaviouralSubtest[]>([])
    // state modal
    const [isOpenFormBehaviouralSubtest, setIsOpenFormBehaviouralSubtest] = useState<boolean>(false);
    const [isOpenModalBehaviouralSubtestSession, setIsOpenModalBehaviouralSubtestSession] = useState<boolean>(false);
    const [isOpenModalBehaviouralScore, setIsOpenModalBehaviouralScore] = useState<boolean>(false);
    // state for modal
    const [selectedBehaviouralSubtest, setSelectedBehaviouralSubtest] = useState<TPSAssessmentBehaviouralSubtest | undefined>(undefined);
    const [selectedBehaviouralSubtestSession, setSelectedBehaviouralSubtestSession] = useState<TPSAssessmentBehaviouralSubtest | undefined>(undefined);

    // get initial data
    const getInitialData = useCallback(
        async function getInitialData() {
            try {
                const ca = new Assessment()
                // fetch assessment psikotest subtest
                setMessage("Fetch Psikotest Subtest")
                let res = await ca.behavioural_subtest(props.assessment_id)
                const list_subtest: TPSAssessmentBehaviouralSubtest[] = res.data.list
                setAssessmentBehaviouralSubtest(list_subtest)
                setMessage("");
            } catch (error) {
                setMessage("");
                const errorMessage = errorHandler(error);
                setNotif({ type: "error", message: errorMessage });
            }
        }, [setNotif, setMessage, props.assessment_id]
    );

    // use effect initial data
    useEffect(() => {
        void props.refreshVersion;
        getInitialData();
    }, [getInitialData, props.refreshVersion]);

    // sortIndexAssessmentBehaviouralSubtest
    const sortIndexAssessmentBehaviouralSubtest = useCallback(
        async function sortIndexAssessmentBehaviouralSubtest() {
            // check subtest length
            const lengthBehaviouralSubtest = assessment_behavioural_subtest.length
            let update = 0;
            for (let index = 0; index < lengthBehaviouralSubtest; index++) {
                const element = assessment_behavioural_subtest[index];
                // check if index is not sequentially
                if (element.index_list !== index) {
                    // update
                    const caps = new AssessmentBehaviouralSubtest()
                    await caps.update({ ...element, index_list: index })
                    update++
                }
            }
            if (update > 0) {
                // re-fetch data
                getInitialData();
            }
        },
        [assessment_behavioural_subtest, getInitialData]
    )

    // when open form behavioural subtest session when behavioural subtest session selected
    useEffect(() => {
        setIsOpenModalBehaviouralSubtestSession(selectedBehaviouralSubtestSession !== undefined)
    }, [selectedBehaviouralSubtestSession]);

    // handleCloseBehaviouralSubtestSession
    const handleCloseBehaviouralSubtestSession = () => {
        // when any selected behavioural
        if (selectedBehaviouralSubtestSession) {
            setSelectedBehaviouralSubtestSession(undefined)
        } else {
            setIsOpenModalBehaviouralSubtestSession(false)
        }
    }

    // always run when any change on assessment_behavioural_subtest
    // to sort index list
    useEffect(() => {
        // re-sort index
        sortIndexAssessmentBehaviouralSubtest();
    }, [assessment_behavioural_subtest, sortIndexAssessmentBehaviouralSubtest])

    // when open form behavioural subtest when behavioural subtest selected
    useEffect(() => {
        setIsOpenFormBehaviouralSubtest(selectedBehaviouralSubtest !== undefined)
    }, [selectedBehaviouralSubtest]);

    // handleCloseBehaviouralSubtest
    const handleCloseBehaviouralSubtest = () => {
        // when any selected behavioural
        if (selectedBehaviouralSubtest) {
            setSelectedBehaviouralSubtest(undefined)
        } else {
            setIsOpenFormBehaviouralSubtest(false)
        }
        // re-fetch data
        getInitialData()
    }

    // handleDeleteBehaviouralSubtest
    const handleDeleteBehaviouralSubtest = async (data: TPSAssessmentBehaviouralSubtest) => {
        try {
            const confirm = await Swal.fire({
                title: "Are you sure?",
                text: "You won't be able to revert this!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: "Yes, delete it!",
            });
            if (confirm.isConfirmed) {
                const cacs = new AssessmentBehaviouralSubtest();
                const res = await cacs.delete(data);
                setNotif({ type: "success", message: res.data.message });
                // re-fetch data
                getInitialData();
            }
        } catch (error) {
            const errorMessage = errorHandler(error);
            setNotif({ type: "error", message: errorMessage });
        }
    }

    // handleMoveBehaviouralSubtest
    const handleMoveBehaviouralSubtest = async (data: TPSAssessmentBehaviouralSubtest, type: "up" | "down") => {
        try {
            setMessage("Change Position Assessment CFIT Subtest")
            const cacs = new AssessmentBehaviouralSubtest()
            // get switched index
            const switched_index = type === "up" ? data.index_list - 1 : data.index_list + 1;
            // get switched data
            const switched_data = assessment_behavioural_subtest.find(v => v.index_list === switched_index)
            if (switched_data) {
                // update switched index
                let res = await cacs.update({ ...switched_data, index_list: data.index_list })
                // update data want to swap
                res = await cacs.update({ ...data, index_list: switched_index })
                setNotif({ type: "success", message: res.data.message });
            }
            setMessage("");
            // re-fetch data
            getInitialData();
        } catch (error) {
            setMessage("");
            const errorMessage = errorHandler(error);
            setNotif({ type: "error", message: errorMessage });
        }
    }

    const renderControl = (data: TPSAssessmentBehaviouralSubtest) => {
        const target = {
            kind: "behavioural" as const,
            subtest_id: data.id,
            subtest_name: data.behavioural_subtest_name,
        };
        const opened = isActiveTarget(props.control, target);
        const pending = props.pendingTarget === activeTargetKey(target);
        return (
            <div className="flex flex-col items-center gap-1">
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${opened ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"}`}>
                    {opened ? "Dibuka" : "Ditutup"}
                </span>
                <button
                    type="button"
                    disabled={!props.control || props.pendingTarget !== null}
                    className={`rounded px-2 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 ${opened ? "bg-red-600" : "bg-blue-600"}`}
                    onClick={() => void props.onChange(target)}
                >
                    {pending ? "Menyimpan..." : opened ? "Tutup subtes" : "Buka subtes"}
                </button>
            </div>
        );
    };

    return (
        <>
            {/* Questionary Form */}
            <Transition
                as={Fragment}
                show={isOpenFormBehaviouralSubtest}
                enter="ease-out duration-50"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-50"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div>
                    <FormBehaviouralSubtest
                        data={selectedBehaviouralSubtest}
                        assessment_id={props.assessment_id}
                        handleClose={() => handleCloseBehaviouralSubtest()}
                        assessment_behavioural_subtest={assessment_behavioural_subtest}
                    />
                </div>
            </Transition>
            {/* Session Modal Session */}
            <Transition
                as={Fragment}
                show={isOpenModalBehaviouralSubtestSession}
                enter="ease-out duration-50"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-50"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div>
                    <ModalBehaviouralSubtestSession
                        data={selectedBehaviouralSubtestSession}
                        assessment_id={props.assessment_id}
                        handleClose={() => handleCloseBehaviouralSubtestSession()}
                        assessment_behavioural_subtest={assessment_behavioural_subtest}
                    />
                </div>
            </Transition>
            {/* Session Modal Session Score */}
            <Transition
                as={Fragment}
                show={isOpenModalBehaviouralScore}
                enter="ease-out duration-50"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-50"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div>
                    <ModalBehaviouralScore
                        doc={props.doc}
                        assessment_id={props.assessment_id}
                        assessment_behavioural_subtest={assessment_behavioural_subtest}
                        handleClose={() => setIsOpenModalBehaviouralScore(false)}
                    />
                </div>
            </Transition>
            <div className="border-2 p-3 border-dashed border-t-0 border-red-900 rounded-b">
                {/** Behavioural Test */}
                <h5 className="font-bold flex pb-2">Behavioural Test</h5>
                <div className="flex">
                    {props.editable &&
                        <button
                            type="button"
                            onClick={() => { setIsOpenFormBehaviouralSubtest(true) }}
                            className="flex items-center bg-green-600 py-1 px-2 text-white rounded">
                            <PlusIcon className="w-4" />
                            <span className="block pl-1">Add Subtest</span>
                        </button>
                    }
                    <button
                        type="button"
                        onClick={() => { setIsOpenModalBehaviouralScore(true) }}
                        className="ml-auto flex items-center bg-blue-600 py-1 px-2 text-white rounded">
                        <ClipboardDocumentCheckIcon className="w-4" />
                        <span className="block pl-1">Score</span>
                    </button>
                </div>
                <div className="pt-2 overflow-auto">
                    <table className="w-full border">
                        <thead className="bg-slate-600 text-white">
                            <tr>
                                <th className="py-1 px-2 w-8 border border-slate-500">No</th>
                                <th className="py-1 px-2 text-left border border-slate-500">Subtes</th>
                                {!props.isPsikolog &&
                                    <>
                                        <th className="py-1 px-2 w-20 border border-slate-500">Status</th>
                                        <th className="py-1 px-2 w-20 border border-slate-500">Sesi</th>
                                    </>
                                }
                                {/** Admin Only - Button Edit & Delete & Move Position */}
                                {isAdmin &&
                                    <>
                                        <th className="py-1 px-2 w-20 border border-slate-500">Edit</th>
                                        <th className="py-1 px-2 w-20 border border-slate-500">Delete</th>
                                        <th className="py-1 px-2 w-10 border border-slate-500">
                                            <div className="flex justify-center"><ArrowsUpDownIcon className="w-4" /></div>
                                        </th>
                                    </>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {assessment_behavioural_subtest.map((v, i) => (
                                <tr key={`behavioural_subtest_${i}`}>
                                    <td className="py-1 px-2 text-center border">{v.index_list + 1}</td>
                                    <td className="py-1 px-2 text-left border">{v.behavioural_subtest_name}</td>
                                    {!props.isPsikolog &&
                                        <>
                                            <td className="py-1 px-2 text-center border">
                                                {renderControl(v)}
                                            </td>
                                            <td className="py-1 px-2 text-center border">
                                                <button
                                                    type="button"
                                                    className="px-2 rounded bg-green-500 text-white"
                                                    onClick={() => setSelectedBehaviouralSubtestSession(v)}>
                                                    Sesi
                                                </button>
                                            </td>
                                        </>
                                    }
                                    {/** Admin Only - Button Edit & Delete & Move Position */}
                                    {isAdmin &&
                                        <>
                                            <td className="py-1 px-2 text-center border">
                                                <button
                                                    type="button"
                                                    className="px-2 rounded bg-blue-500 text-white"
                                                    onClick={() => setSelectedBehaviouralSubtest(v)}>
                                                    Edit
                                                </button>
                                            </td>
                                            <td className="py-1 px-2 text-center border">
                                                <button
                                                    type="button"
                                                    className="px-2 rounded bg-red-500 text-white"
                                                    onClick={() => handleDeleteBehaviouralSubtest(v)}>
                                                    Delete
                                                </button>
                                            </td>
                                            <td className="py-1 px-2 text-center border">
                                                <div className="flex flex-col space-y-1">
                                                    {v.index_list > 0 &&
                                                        <button
                                                            type="button"
                                                            className="px-2 rounded bg-white-500 hover:bg-slate-100 border flex justify-center"
                                                            onClick={() => handleMoveBehaviouralSubtest(v, "up")}>
                                                            <ChevronUpIcon className="w-3" />
                                                        </button>
                                                    }
                                                    {v.index_list !== (assessment_behavioural_subtest.length - 1) &&
                                                        <button
                                                            type="button"
                                                            className="px-2 rounded bg-white-500 hover:bg-slate-100 border justify-cente"
                                                            onClick={() => handleMoveBehaviouralSubtest(v, "down")}>
                                                            <ChevronDownIcon className="w-3" />
                                                        </button>
                                                    }
                                                </div>
                                            </td>
                                        </>
                                    }
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    )
}

export default ListBehavioural;